package one.healthium.ui.compose.components.banner

import androidx.compose.animation.AnimatedVisibility
import androidx.compose.animation.core.FastOutSlowInEasing
import androidx.compose.animation.core.tween
import androidx.compose.animation.slideInVertically
import androidx.compose.animation.slideOutVertically
import androidx.compose.foundation.background
import androidx.compose.foundation.gestures.AnchoredDraggableDefaults
import androidx.compose.foundation.gestures.AnchoredDraggableState
import androidx.compose.foundation.gestures.DraggableAnchors
import androidx.compose.foundation.gestures.Orientation
import androidx.compose.foundation.gestures.anchoredDraggable
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.offset
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.statusBarsPadding
import androidx.compose.foundation.layout.widthIn
import androidx.compose.foundation.layout.wrapContentHeight
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableFloatStateOf
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.draw.shadow
import androidx.compose.ui.layout.onSizeChanged
import androidx.compose.ui.unit.IntOffset
import androidx.compose.ui.unit.dp
import androidx.compose.ui.util.fastForEachIndexed
import kotlinx.coroutines.delay
import one.healthium.ui.compose.components.adaptive.AppAdaptiveDefaults
import one.healthium.ui.compose.theme.AppTheme
import kotlin.math.roundToInt

private enum class DragAnchors {
    Start,
    Dismissed
}

@Composable
internal fun <T : BannerData> BannerHost(
    hostState: BannerHostState<T>,
    bannerContent: @Composable (T) -> Unit,
    modifier: Modifier = Modifier
) {
    val banners by hostState.banners.collectAsState()
    val bannerStyle = AppTheme.bannerStyle

    Box(modifier = modifier) {
        banners.fastForEachIndexed { index, bannerData ->
            var bannerHeightPx by remember(bannerData.id) { mutableFloatStateOf(0f) }
            var isVisible by remember(bannerData.id) { mutableStateOf(false) }

            LaunchedEffect(bannerData.id) {
                delay(50) // Небольшая задержка для корректной анимации
                isVisible = true
            }

            val anchors = remember(bannerHeightPx) {
                if (bannerHeightPx > 0f) {
                    DraggableAnchors {
                        DragAnchors.Start at 0f
                        DragAnchors.Dismissed at -bannerHeightPx
                    }
                } else {
                    DraggableAnchors {
                        DragAnchors.Start at 0f
                    }
                }
            }

            val anchoredDraggableState = remember(bannerData.id, anchors) {
                AnchoredDraggableState(
                    initialValue = DragAnchors.Start,
                    anchors = anchors
                )
            }

            LaunchedEffect(anchoredDraggableState.currentValue) {
                if (anchoredDraggableState.currentValue == DragAnchors.Dismissed) {
                    isVisible = false
                    delay(300) // Ждем завершения анимации выхода
                    hostState.dismissBanner(bannerData.id)
                }
            }

            bannerData.autoDismissDuration?.let { duration ->
                LaunchedEffect(bannerData.id) {
                    delay(duration)
                    isVisible = false
                    delay(300) // Ждем завершения анимации выхода
                    hostState.dismissBanner(bannerData.id)
                }
            }

            val stackOffset = if (index == 0) 0.dp else 8.dp

            AnimatedVisibility(
                visible = isVisible,
                enter = slideInVertically(
                    animationSpec = tween(300, easing = FastOutSlowInEasing),
                    initialOffsetY = { -it }
                ),
                exit = slideOutVertically(
                    animationSpec = tween(300, easing = FastOutSlowInEasing),
                    targetOffsetY = { -it }
                ),
                modifier = Modifier.offset(y = stackOffset)
            ) {
                Box(
                    modifier = Modifier
                        .widthIn(max = AppAdaptiveDefaults.maxWidthTwoPaneMode)
                        .fillMaxWidth()
                        .wrapContentHeight()
                        .statusBarsPadding()
                        .padding(24.dp)
                        .onSizeChanged { size ->
                            bannerHeightPx = size.height.toFloat()
                        }
                        .offset {
                            IntOffset(
                                x = 0,
                                y = anchoredDraggableState
                                    .requireOffset()
                                    .roundToInt()
                                    .coerceAtMost(0)
                            )
                        }
                        .anchoredDraggable(
                            state = anchoredDraggableState,
                            orientation = Orientation.Vertical,
                            flingBehavior = AnchoredDraggableDefaults.flingBehavior(
                                state = anchoredDraggableState,
                                positionalThreshold = { distance -> distance * 0.7f }
                            )
                        )
                        .shadow(bannerStyle.elevationOrDefault, bannerStyle.shapeOrDefault)
                        .background(bannerStyle.containerColorOrDefault, bannerStyle.shapeOrDefault)
                        .clip(bannerStyle.shapeOrDefault)
                ) {
                    bannerContent(bannerData)
                }
            }
        }
    }
}
